import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DtrImportDialog } from "@/components/DtrImportDialog";

const timeOf = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Manila" }) : "—";
const statusStyle = { "On-Time": "bg-emerald-50 text-emerald-700", Early: "bg-blue-50 text-blue-700", Late: "bg-red-50 text-red-700", Flexible: "bg-purple-50 text-purple-700", Present: "bg-emerald-50 text-emerald-700" };
const tagStyle = (t) => t.includes("Leave") ? "bg-teal-50 text-teal-700" : t.startsWith("Late") ? "bg-red-50 text-red-700" : t.includes("Holiday") ? "bg-rose-50 text-rose-700" : t.includes("Rest Day") ? "bg-amber-50 text-amber-700" : t.endsWith("OT") ? "bg-indigo-50 text-indigo-700" : ["DTR Correction", "OB", "WFH", "Imported"].includes(t) ? "bg-sky-50 text-sky-700" : statusStyle[t] || "bg-slate-100 text-slate-600";
const errMsg = (e, d) => e.response?.data?.detail || d;
const manilaToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const firstDay = () => `${manilaToday().slice(0, 8)}01`;
const todayStr = manilaToday;

function EmpSubtotal({ rows, name }) {
  const s = (fn) => Math.round(rows.reduce((a, r) => a + (fn(r) || 0), 0) * 100) / 100;
  const h = (n) => (n ? `${n.toLocaleString()}h` : "—");
  const id = rows[0]?.employee_id;
  return (
    <TableRow className="bg-blue-50/70 font-semibold border-b-2 border-blue-200" data-testid={`dtr-subtotal-${id}`}>
      <TableCell colSpan={7} className="text-xs text-blue-900">Subtotal · {name} <span className="font-normal text-blue-700">({rows.length} day{rows.length === 1 ? "" : "s"})</span></TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-work-${id}`}>{h(s((r) => r.work_hours))}</TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-lates-${id}`}>{h(s((r) => (r.late_minutes || 0) / 60))}</TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-undertime-${id}`}>{h(s((r) => r.undertime_hours))}</TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-overtime-${id}`}>{h(s((r) => r.overtime_hours))}</TableCell>
      <TableCell className="text-xs">{h(s((r) => r.approved_overtime))}</TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-nd-${id}`}>{h(s((r) => r.night_diff))}</TableCell>
      <TableCell className="text-xs" data-testid={`dtr-subtotal-otnd-${id}`}>{h(s((r) => r.ot_night_diff))}</TableCell>
      <TableCell colSpan={4}></TableCell>
    </TableRow>
  );
}

export default function DtrReport() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState("");
  const [start, setStart] = useState(firstDay());
  const [end, setEnd] = useState(todayStr());
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [period, setPeriod] = useState(null);
  const [sentBack, setSentBack] = useState(null);
  const [busyId, setBusyId] = useState("");
  const { user } = useAuth();
  const reqSeq = useRef(0);
  const canUnverify = (user?.permissions || []).includes("*") || ["SUPER ADMIN", "HR ADMIN"].includes((user?.role || "").toUpperCase());

  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);
  useEffect(() => {
    const qs = companyId ? `?company_id=${companyId}` : "?";
    api.get(`/employees${qs}`).then(({ data }) => {
      setEmps(data);
      setEmpId(data[0]?.id || "");
      if (!data.length) setData(null);
    }).catch(() => {});
  }, [companyId]);
  const [q, setQ] = useState(null);
  const dirty = !q || q.companyId !== companyId || q.empId !== empId || q.start !== start || q.end !== end;
  const generate = () => {
    if (!empId) { toast.error("Select an employee"); return; }
    if (!start || !end || start > end) { toast.error("Pick a valid From and To date"); return; }
    setData(null); setQ({ companyId, empId, start, end });
  };
  const run = (p = 1) => {
    const pg = Number.isInteger(p) ? p : 1;
    if (!q) return;
    const { companyId, empId, start, end } = q;
    const seq = ++reqSeq.current;
    const fresh = (fn) => (res) => { if (seq === reqSeq.current) fn(res); };
    const qp = new URLSearchParams({ start, end, company_id: companyId, employee_id: empId });
    api.get(`/attendance/dtr-periods?${qp}`).then(fresh(({ data }) => { setPeriod(data?.status === "Ready for Payroll" ? data : null); setSentBack(data?.status === "Sent Back" ? data : null); })).catch(fresh(() => { setPeriod(null); setSentBack(null); }));
    const url = empId === "__all__"
      ? `/attendance/dtr-bulk?${companyId ? `company_id=${companyId}&` : ""}start=${start}&end=${end}&page=${pg}&size=50`
      : `/attendance/dtr?employee_id=${empId}&start=${start}&end=${end}`;
    api.get(url).then(fresh(({ data }) => setData(data))).catch(() => {});
  };
  useEffect(() => { if (q) run(); /* eslint-disable-next-line */ }, [q]);

  const patchRow = (id, p) => setData((d) => ({ ...d, records: d.records.map((r) => (r.id === id ? { ...r, ...p } : r)) }));
  const verify = async (r) => {
    setBusyId(r.id);
    try { const { data: p } = await api.post(`/attendance/dtr/${r.id}/verify`); patchRow(r.id, p); } catch (e) { toast.error(errMsg(e, "Verify failed")); }
    setBusyId("");
  };
  const unverify = async (r) => {
    setBusyId(r.id);
    try { await api.post(`/attendance/dtr/${r.id}/unverify`); patchRow(r.id, { verified: false, verified_by: null, verified_at: null }); setPeriod(null); toast.success("Row unlocked"); } catch (e) { toast.error(errMsg(e, "Un-verify failed")); }
    setBusyId("");
  };
  const verifyAll = async () => {
    const pending = data.records.filter((r) => !r.verified && !r.leave_only).length;
    if (!window.confirm(`Verify and lock all ${pending} unverified row(s) in this period?`)) return;
    setBusyId("__all__");
    try {
      const { data: p } = await api.post("/attendance/dtr-verify-all", { company_id: q.companyId, employee_id: q.empId, start: q.start, end: q.end });
      setData((d) => ({ ...d, records: d.records.map((r) => (r.verified || r.leave_only ? r : { ...r, verified: true, verified_by: p.verified_by, verified_at: p.verified_at })) }));
      toast.success(`${p.count} row(s) verified`);
    } catch (e) { toast.error(errMsg(e, "Verify all failed")); }
    setBusyId("");
  };
  const [proceeding, setProceeding] = useState(false);
  const proceed = async () => {
    setProceeding(true);
    try {
      const p = await runJob(() => api.post("/attendance/dtr-finalize?background=true", { company_id: q.companyId, employee_id: q.empId, start: q.start, end: q.end }).then((r) => r.data));
      setPeriod(p); setSentBack(null); toast.success(`DTR finalized — ${p.computation_rows || 0} employee(s) sent to Attendance Computation`);
    } catch { /* toast shown by runJob */ }
    setProceeding(false);
  };
  const emp = emps.find((e) => e.id === q?.empId);
  const Thumb = ({ rec, kind, label }) => {
    const inline = rec[`clock_${kind}_photo`];
    if (!inline && !rec[`has_${kind}_photo`]) return <span className="text-slate-300">—</span>;
    const open = async () => {
      if (inline) { setPreview(inline); return; }
      try { const { data: s } = await api.get(`/attendance/snapshot/${rec.id}`); if (s[kind]) setPreview(s[kind]); else toast.error("Selfie not available"); }
      catch { toast.error("Could not load selfie"); }
    };
    return <button type="button" onClick={open} title={`View ${label} selfie`} className="h-7 w-7 shrink-0 rounded border border-slate-200 bg-slate-50 hover:bg-blue-50 inline-flex items-center justify-center" data-testid={`dtr-selfie-${kind}-${rec.id}`}><Icons.Camera className="h-3.5 w-3.5 text-slate-500" /></button>;
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Daily Time Record (DTR)" subtitle="Per-employee attendance with in/out, lateness, overtime and clock-in selfies."
        actions={(!user?.hris_limited || (user?.hris_perms || []).includes("tk.import")) && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="dtr-import-open"><Icons.Upload className="h-4 w-4 mr-1.5" />Import DTR</Button>} />
      <DtrImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={run} />
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5"><Label className="text-xs">Company</Label>
          <Select value={companyId || "__all__"} onValueChange={(v) => setCompanyId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="dtr-company"><SelectValue placeholder="All companies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Employee</Label>
          <Select value={empId} onValueChange={setEmpId}><SelectTrigger data-testid="dtr-employee"><SelectValue placeholder={emps.length ? "Select employee…" : "No employees"} /></SelectTrigger><SelectContent><SelectItem value="__all__">All employees</SelectItem>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_no}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dtr-start" /></div>
        <div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dtr-end" /></div>
        <Button onClick={generate} disabled={!empId} className={dirty ? "" : "opacity-80"} data-testid="dtr-generate-btn"><Icons.Search className="h-4 w-4 mr-1.5" />Generate</Button>
        {q && dirty && <p className="sm:col-span-2 lg:col-span-5 text-xs text-amber-700" data-testid="dtr-filter-changed">Filters changed — click Generate to refresh the report.</p>}
      </div>
      {!q && <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="dtr-generate-hint">Choose the company, employee and cut-off dates, then click <b>Generate</b>.</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[["Days", data.summary.days, Icons.CalendarDays], ["Total Hours", data.summary.hours + "h", Icons.Clock], ["Overtime", data.summary.overtime + "h", Icons.TrendingUp], ["Late Days", `${data.summary.late_days} · ${(data.summary.late_hours || 0).toFixed(2)}h`, Icons.AlertTriangle]].map(([l, v, I]) => (
              <div key={l} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3"><div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><I className="h-4.5 w-4.5 text-slate-600" /></div><div><div className="text-xl font-semibold text-slate-900">{v}</div><div className="text-xs text-slate-500">{l}</div></div></div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table className="text-xs [&_td]:px-1.5 [&_th]:px-1.5" wrapperClassName="max-h-[calc(100vh-180px)]" data-testid="dtr-table-scroll">
              <TableHeader className="sticky top-0 z-20 shadow-[0_1px_0_0_rgb(226,232,240)]" data-testid="dtr-table-header">
                <TableRow className="bg-slate-50 hover:bg-slate-50 [&_th]:bg-slate-50 [&_th]:text-[11px] [&_th]:leading-tight [&_th]:h-auto [&_th]:py-2">
                  <TableHead>User ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Day</TableHead>
                  <TableHead>Time In</TableHead>
                  <TableHead>Time Out</TableHead>
                  <TableHead>Work</TableHead>
                  <TableHead>Lates</TableHead>
                  <TableHead>Undertime</TableHead>
                  <TableHead>Overtime</TableHead>
                  <TableHead>Approved Overtime</TableHead>
                  <TableHead>Night Diff</TableHead>
                  <TableHead>OT Night Diff</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead>Selfies</TableHead>
                  <TableHead>Verification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="dtr-table">
                {data.records.length === 0 && <TableRow><TableCell colSpan={18} className="text-center text-slate-400 py-8">No records for {emp ? `${emp.first_name} ${emp.last_name}` : "this employee"} in range.</TableCell></TableRow>}
                {data.records.map((r, i) => (
                  <React.Fragment key={r.id}>
                  <TableRow data-testid={`dtr-row-${r.date}`} className={r.verified ? "bg-emerald-50/40" : ""}>
                    <TableCell className="text-xs font-mono text-slate-600">{r.user_id || "—"}</TableCell>
                    <TableCell className="text-xs font-medium text-slate-800 min-w-[90px]">{r.name || "—"}</TableCell>
                    <TableCell className="text-[11px] text-slate-600 min-w-[80px]">{r.schedule_label || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.day || "—"}</TableCell>
                    <TableCell className="font-mono text-[11px] whitespace-nowrap">{timeOf(r.clock_in)}</TableCell>
                    <TableCell className="font-mono text-[11px] whitespace-nowrap">{timeOf(r.clock_out)}</TableCell>
                    <TableCell className="text-sm">{r.work_hours ? `${r.work_hours}h` : "—"}</TableCell>
                    <TableCell className="text-sm" data-testid={`dtr-late-${r.id}`}>{r.late_minutes ? `${(Math.round((r.late_minutes / 60) * 100) / 100).toFixed(2)}h` : "—"}</TableCell>
                    <TableCell className="text-sm">{r.undertime_hours ? `${r.undertime_hours}h` : "—"}</TableCell>
                    <TableCell className="text-sm">{r.overtime_hours ? `${r.overtime_hours}h` : "—"}</TableCell>
                    <TableCell className="text-sm">{r.approved_overtime ? `${r.approved_overtime}h` : "—"}</TableCell>
                    <TableCell className="text-sm">{r.night_diff ? `${r.night_diff}h` : "—"}</TableCell>
                    <TableCell className="text-sm">{r.ot_night_diff ? `${r.ot_night_diff}h` : "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.device || "—"}</TableCell>
                    <TableCell data-testid={`dtr-remarks-${r.id}`} title={r.holiday_name || ""}><div className="flex flex-wrap gap-1 min-w-[110px] max-w-[170px]">{(r.remark_tags?.length ? r.remark_tags : [r.status || "—"]).map((t) => <Badge key={t} className={`${tagStyle(t)} hover:opacity-100 text-[10px]`}>{t}</Badge>)}</div></TableCell>
                    <TableCell><div className="flex gap-1 whitespace-nowrap"><Thumb rec={r} kind="in" label="in" /><Thumb rec={r} kind="out" label="out" /></div></TableCell>
                    <TableCell>{r.leave_only ? <span className="text-xs text-slate-400" data-testid={`dtr-leave-only-${r.id}`}>{["unpaid", "unapproved"].includes(r.leave_kind) ? "Absent" : "On leave"}</span> : r.verified ? (
                      <div className="flex items-center gap-0.5" title={`Verified by ${r.verified_by || "—"} · No error`}>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:opacity-100 text-[10px] px-1.5 flex-col items-start leading-tight" data-testid={`dtr-verified-${r.id}`}><span className="flex items-center"><Icons.Lock className="h-3 w-3 mr-1" />Verified</span><span className="text-[9px] font-normal">No error</span></Badge>
                        {canUnverify && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={busyId === r.id} onClick={() => unverify(r)} data-testid={`dtr-unverify-${r.id}`}><Icons.Unlock className="h-3.5 w-3.5" /></Button>}
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busyId === r.id} onClick={() => verify(r)} data-testid={`dtr-verify-${r.id}`}><Icons.ShieldCheck className="h-3.5 w-3.5 mr-1" />Verify</Button>
                    )}</TableCell>
                  </TableRow>
                  {q?.empId === "__all__" && data.records[i + 1]?.employee_id !== r.employee_id && <EmpSubtotal rows={data.records.filter((x) => x.employee_id === r.employee_id)} name={r.name} />}
                  </React.Fragment>
                ))}
              </TableBody>
              {data.records.length > 0 && (() => {
                const sum = (fn) => data.records.reduce((a, r) => a + (fn(r) || 0), 0);
                const hrs = (n) => `${(Math.round(n * 100) / 100).toLocaleString()} hrs`;
                const t = {
                  work: sum((r) => r.work_hours),
                  lates: sum((r) => (r.late_minutes || 0) / 60),
                  undertime: sum((r) => r.undertime_hours),
                  overtime: sum((r) => r.overtime_hours),
                  approved_ot: sum((r) => r.approved_overtime),
                  night: sum((r) => r.night_diff),
                  ot_night: sum((r) => r.ot_night_diff),
                };
                return (
                  <TableFooter>
                    <TableRow className="bg-slate-100 whitespace-nowrap font-semibold" data-testid="dtr-total-row">
                      <TableCell className="text-sm text-slate-800">Total</TableCell>
                      <TableCell colSpan={6}></TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-work">{hrs(t.work)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-lates">{hrs(t.lates)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-undertime">{hrs(t.undertime)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-overtime">{hrs(t.overtime)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-approved-ot">{hrs(t.approved_ot)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-night-diff">{hrs(t.night)}</TableCell>
                      <TableCell className="text-sm" data-testid="dtr-total-ot-night-diff">{hrs(t.ot_night)}</TableCell>
                      <TableCell colSpan={4}></TableCell>
                    </TableRow>
                  </TableFooter>
                );
              })()}
            </Table>
          </div>
          {q?.empId === "__all__" && data.pages > 1 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600" data-testid="dtr-pager">
              <span>Employees {(data.page - 1) * data.size + 1}–{Math.min(data.page * data.size, data.total_employees)} of {data.total_employees} · totals above are for this page</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => run(data.page - 1)} data-testid="dtr-page-prev">Previous</Button>
                <span data-testid="dtr-page-label">Page {data.page} of {data.pages}</span>
                <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => run(data.page + 1)} data-testid="dtr-page-next">Next</Button>
              </div>
            </div>
          )}
          {data.records.length > 0 && sentBack && !period && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="dtr-sent-back-banner">
              <Icons.Undo2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div><b>Sent back by Attendance Computation</b> · {sentBack.sent_back_by} · {(sentBack.sent_back_at || "").slice(0, 10)}<div className="text-amber-800">“{sentBack.sent_back_reason}” — correct the rows, verify them again and click Proceed to resend.</div></div>
            </div>
          )}
          {data.records.length > 0 && (() => {
            const workRows = data.records.filter((r) => !r.leave_only);
            const pending = workRows.filter((r) => !r.verified).length;
            return (
              <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="dtr-proceed-bar">
                {pending > 0 && <Button variant="outline" onClick={verifyAll} disabled={busyId === "__all__"} data-testid="dtr-verify-all-btn"><Icons.ShieldCheck className="h-4 w-4 mr-1.5" />Verify all ({pending})</Button>}
                <Button onClick={proceed} disabled={proceeding || !!period || pending > 0 || !workRows.length} data-testid="dtr-proceed-btn"><Icons.ArrowRightCircle className="h-4 w-4 mr-1.5" />{proceeding ? "Processing…" : "Proceed"}</Button>
                {period ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:opacity-100" data-testid="dtr-period-status">Finalized · Ready for Payroll · by {period.finalized_by}</Badge>
                ) : (
                  <span className="text-xs text-slate-500" data-testid="dtr-pending-count">{pending ? `${pending} of ${workRows.length} row(s) still need verification` : workRows.length ? "All rows verified — ready to finalize" : "No attendance rows to finalize"}</span>
                )}
              </div>
            );
          })()}
        </>
      )}

      {preview && <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="dtr-photo-preview"><img src={preview} alt="selfie" className="max-h-[80vh] rounded-xl" /></div>}
    </div>
  );
}
